import React, { createContext, useContext, useState, useEffect } from 'react';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const [siteData, setSiteData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [siteRes, productsRes] = await Promise.all([
          fetch('/data/site.json'),
          fetch('/data/products.json')
        ]);

        if (!siteRes.ok || !productsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const site = await siteRes.json();
        const productsData = await productsRes.json();

        setSiteData(site);
        setProducts(productsData.products || []);
      } catch (err) {
        console.error('Error fetching site data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getProductsByCategory = (categoryId) => {
    return products.filter(p => p.category === categoryId);
  };

  const getProductsByTag = (tag) => {
    return products.filter(p => p.tags && p.tags.includes(tag));
  };

  const getNewProducts = () => {
    return products.filter(p => p.isNew || p.tags?.includes('novidade'));
  };

  const getBestSellers = () => {
    return products.filter(p => p.isBestSeller || p.tags?.includes('mais-vendido'));
  };

  const getKits = () => {
    return products.filter(p => p.category === 'kits');
  };

  const getThemeProducts = () => {
    return products.filter(p => p.category === 'temas' || p.tags?.includes('tema'));
  };

  const getProductById = (id) => {
    return products.find(p => p.id === id);
  };

  const searchProducts = (query) => {
    const searchTerm = query.toLowerCase();
    return products.filter(p => 
      p.name.toLowerCase().includes(searchTerm) ||
      p.description?.toLowerCase().includes(searchTerm) ||
      p.tags?.some(tag => tag.toLowerCase().includes(searchTerm)) ||
      p.category?.toLowerCase().includes(searchTerm)
    );
  };

  const getRelatedProducts = (product, limit = 4) => {
    if (!product) return [];
    
    const related = products.filter(p => 
      p.id !== product.id && 
      (p.category === product.category || 
       p.tags?.some(tag => product.tags?.includes(tag)))
    );
    
    return related.slice(0, limit);
  };

  const formatPrice = (price) => {
    return price.toFixed(2).replace('.', ',') + ' €';
  };

  const value = {
    siteData,
    products,
    loading,
    error,
    getProductsByCategory,
    getProductsByTag,
    getNewProducts,
    getBestSellers,
    getKits,
    getThemeProducts,
    getProductById,
    searchProducts,
    getRelatedProducts,
    formatPrice
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};
